// Creator-side wiki management: create/configure the project's wiki, then
// author its pages (flat list + sidebar nav, markdown + live preview - see
// project decisions in the wiki feature review). Ported to match the shape
// of ScreenProjectPublish (project/screens-project-ops.tsx): the same
// set-card sections, the same explicit-save posture (no autosave/debounce -
// project/screens-project-ops.tsx's own history flagged "flush saves on
// unmount" as a real bug class; an explicit Save button sidesteps it
// entirely for this first version).
import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent } from 'react';
import { timeAgo } from '../app/screens-app';
import { CenteredLoading } from '../components/ui-shared';
import { uploadWikiImage } from '../assets/wikiImageUpload';
import { EdTypedConfirmDialog, EdConfirmDialog } from '../editor/editor-panels';
import { TabStrip, panelId, type TabDescriptor } from '../../engine/editor/components/Tabs';
import { confirmNavigation, PjIcon, PJ_ICONS, PjShell, useUnsavedChangesGuard, useWorkspace } from '../project/screens-project';
import {
  createWiki,
  createWikiPage,
  deleteWiki,
  deleteWikiPage,
  getWiki,
  getWikiPage,
  listWikiPages,
  listWikiPageVersions,
  reorderWikiPages,
  restoreWikiPageVersion,
  updateWiki,
  updateWikiPage,
  type Wiki,
  type WikiPage,
  type WikiPageSummary,
  type WikiPageVersion,
  type WikiVisibility,
} from '../services/wiki';
import { MarkdownView } from './MarkdownView';

const VISIBILITY_UI: { id: string; wire: WikiVisibility; hint: string }[] = [
  { id: 'Public', wire: 'public', hint: 'Listed at its own URL, anyone with the link can read it.' },
  { id: 'Unlisted', wire: 'unlisted', hint: 'Reachable by link only.' },
  { id: 'Off', wire: 'private', hint: "The wiki URL shows as unavailable, players can't tell it apart from a link that never existed." },
];

/** True once the project has confirmed it either has, or does not have, a
 * wiki - distinguishes "still loading" from "confirmed none exists" so the
 * UI never flashes the "create a wiki" panel while the real answer is still
 * in flight. */
type WikiLoadState = { status: 'loading' } | { status: 'none' } | { status: 'ready'; wiki: Wiki };

function CreateWikiPanel({ onCreated }: { onCreated: (wiki: Wiki) => void }) {
  const { projectId } = useWorkspace();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const wiki = await createWiki(projectId, {});
      onCreated(wiki);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the wiki.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="set-card">
      <h3>No wiki yet</h3>
      <span className="hint">
        Create a wiki to host lore, guides and reference pages for your game at their own public URL, independent
        of whether the game itself is published.
      </span>
      <button className="ss-btn ss-btn--primary ss-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => void create()} disabled={busy}>
        {busy ? 'Creating…' : 'Create wiki'}
      </button>
      {error && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Compact clipboard chip for the wiki's own URL - CopyField's clipboard
 * logic in a much slimmer shell (an inline pill, not a full ss-input row),
 * since this now has to fit inside a single-line settings bar rather than
 * a spacious set-card. */
function WikiUrlChip({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const display = url.replace(/^https?:\/\//, '');
  return (
    <button
      type="button"
      className="ss-btn ss-btn--ghost ss-btn--sm"
      title="Copy wiki URL"
      onClick={() => {
        void navigator.clipboard?.writeText(url).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      style={{
        gap: 7,
        padding: '6px 12px',
        borderRadius: 999,
        fontFamily: 'var(--ss-mono)',
        fontSize: 11.5,
        fontWeight: 500,
        color: 'var(--ss-text-2)',
        minWidth: 0,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
        {copied ? 'Copied' : display}
      </span>
      <PjIcon d={PJ_ICONS.copy!} size={11} />
    </button>
  );
}

/** Condensed to a single slim bar when idle - the URL, a live visibility
 * control and a "View live" link are always visible; renaming and deleting
 * the wiki (set-once-and-forget actions, rarely touched again after initial
 * setup) sit behind a disclosure toggle instead of permanently occupying a
 * quarter of the screen above the editor. */
function WikiSettingsBar({ wiki, onChanged, onDeleted }: { wiki: Wiki; onChanged: (wiki: Wiki) => void; onDeleted: () => void }) {
  const { projectId } = useWorkspace();
  const [title, setTitle] = useState(wiki.title);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => setTitle(wiki.title), [wiki.title]);

  async function saveTitle() {
    const trimmed = title.trim();
    if (!trimmed || trimmed === wiki.title) return;
    setSavingTitle(true);
    setError(null);
    try {
      onChanged(await updateWiki(projectId, { title: trimmed }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not rename the wiki.');
    } finally {
      setSavingTitle(false);
    }
  }

  async function setVisibility(wire: WikiVisibility) {
    if (wiki.visibility === wire) return;
    setError(null);
    try {
      onChanged(await updateWiki(projectId, { visibility: wire }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change visibility.');
    }
  }

  async function doDelete() {
    setConfirmingDelete(false);
    setError(null);
    try {
      await deleteWiki(projectId);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the wiki.');
    }
  }

  return (
    <div style={{ flexShrink: 0, borderBottom: '1px solid var(--ss-border-soft)', background: 'var(--ss-panel)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ss-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
          {wiki.title}
        </span>
        <WikiUrlChip url={wiki.url} />
        <div role="group" aria-label="Wiki visibility" style={{ display: 'flex', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: 2, gap: 2 }}>
          {VISIBILITY_UI.map((o) => {
            const selected = wiki.visibility === o.wire;
            return (
              <button
                key={o.id}
                type="button"
                title={o.hint}
                aria-pressed={selected}
                onClick={() => void setVisibility(o.wire)}
                style={{
                  border: 'none',
                  borderRadius: 999,
                  padding: '5px 11px',
                  fontSize: 11.5,
                  fontWeight: selected ? 600 : 500,
                  color: selected ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                  background: selected ? 'var(--ss-accent-soft)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                {o.id}
              </button>
            );
          })}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <a
            className="ss-btn ss-btn--ghost ss-btn--sm"
            href={wiki.url}
            target="_blank"
            rel="noreferrer"
            title="View live wiki"
            aria-label="View live wiki"
            style={{ padding: '6px 9px', borderRadius: 999 }}
          >
            <PjIcon d={PJ_ICONS.external!} size={13} />
          </a>
          <button
            type="button"
            className="ss-btn ss-btn--ghost ss-btn--sm"
            title={expanded ? 'Hide wiki settings' : 'More wiki settings'}
            aria-label={expanded ? 'Hide wiki settings' : 'More wiki settings'}
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
            style={{ padding: '6px 9px', borderRadius: 999, color: expanded ? 'var(--ss-accent)' : undefined }}
          >
            <PjIcon d={PJ_ICONS.settings!} size={13} />
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '2px 20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', maxWidth: 420 }}>
            <input
              className="ss-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => void saveTitle()}
              placeholder="Wiki title"
              style={{ padding: '8px 12px', fontSize: 13 }}
            />
            {savingTitle && <span className="hint">Saving…</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {VISIBILITY_UI.map((o) => (
              <span key={o.id} className="hint">
                <strong style={{ color: 'var(--ss-text-2)' }}>{o.id}:</strong> {o.hint}
              </span>
            ))}
          </div>
          <button
            type="button"
            className="ss-btn ss-btn--danger ss-btn--sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete wiki
          </button>
        </div>
      )}

      {error && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 13, padding: '0 20px 12px' }} role="alert">
          {error}
        </p>
      )}
      {confirmingDelete && (
        <EdTypedConfirmDialog
          title="Delete this wiki?"
          body="Every page and the wiki URL are removed permanently - this can't be undone."
          confirmLabel="Delete wiki"
          requiredText="DELETE"
          onConfirm={() => void doDelete()}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </div>
  );
}

function VersionHistoryPanel({ projectId, page, onRestored }: { projectId: string; page: WikiPage; onRestored: (page: WikiPage) => void }) {
  const [versions, setVersions] = useState<WikiPageVersion[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setVersions(null);
    setLoadError(null);
    listWikiPageVersions(projectId, page.id)
      .then((v) => {
        if (!cancelled) setVersions(v);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not load version history.');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, page.id]);

  async function restore(version: number) {
    if (!window.confirm(`Restore version ${version}? This replaces the page's current content. You can undo by restoring the version you're on now.`)) {
      return;
    }
    setRestoring(version);
    setRestoreError(null);
    try {
      onRestored(await restoreWikiPageVersion(projectId, page.id, version));
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Could not restore that version.');
    } finally {
      setRestoring(null);
    }
  }

  return (
    <div style={{ minHeight: 320, padding: '4px 2px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {versions === null && !loadError && <span className="hint">Loading version history…</span>}
      {loadError && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">
          {loadError}
        </p>
      )}
      {versions?.length === 0 && <span className="hint">No saved versions yet.</span>}
      {versions?.map((v) => (
        <div key={v.version} className="ss-row" style={{ alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12, color: v.live ? 'var(--ss-accent)' : 'var(--ss-text-3)', width: 36 }}>
            v{v.version}
          </span>
          <span style={{ flex: 1, color: 'var(--ss-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</span>
          <span style={{ fontSize: 12, color: 'var(--ss-text-3)', whiteSpace: 'nowrap' }}>
            {(v.created_at ? timeAgo(new Date(v.created_at).getTime()) : '') + (v.live ? ' · Current' : '')}
          </span>
          {!v.live && (
            <button className="ss-btn ss-btn--ghost ss-btn--sm" disabled={restoring !== null} onClick={() => void restore(v.version)}>
              {restoring === v.version ? 'Restoring…' : 'Restore'}
            </button>
          )}
        </div>
      ))}
      {restoreError && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">
          {restoreError}
        </p>
      )}
    </div>
  );
}

// Mirrors the api's _BODY_MAX_CHARS (see wiki.py) - display only, so the
// creator can see how close a page is to the limit while editing; the server
// remains the authority and enforces its own cap regardless of this number.
const BODY_CHAR_SOFT_CAP = 100_000;

type EditorMode = 'edit' | 'split' | 'preview' | 'history';
const VIEW_MODES: TabDescriptor[] = [
  { id: 'edit', label: 'Edit' },
  { id: 'split', label: 'Split' },
  { id: 'preview', label: 'Preview' },
];

const TOOLBAR_BTN_STYLE: CSSProperties = { minWidth: 30, padding: '5px 9px', fontSize: 12.5 };

function PageEditor({
  page,
  onSaved,
  onDeleted,
  onDirtyChange,
}: {
  page: WikiPage;
  onSaved: (page: WikiPage) => void;
  onDeleted: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { projectId } = useWorkspace();
  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [section, setSection] = useState(page.section ?? '');
  const [body, setBody] = useState(page.body_markdown);
  const [mode, setMode] = useState<EditorMode>('edit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  // A toolbar action that wants to leave a specific range selected (e.g.
  // "https://" ready to type over) can't just call setSelectionRange right
  // after setBody: requestAnimationFrame races React's own DOM commit for a
  // controlled textarea, and a commit landing after the RAF resets the caret
  // to the end. Recording the desired range here and applying it in the
  // effect below - guaranteed to run after the commit for the render that
  // produced this `body` - makes it land every time instead of most times.
  const pendingSelectionRef = useRef<[number, number] | null>(null);

  useEffect(() => {
    setTitle(page.title);
    setSlug(page.slug);
    setSection(page.section ?? '');
    setBody(page.body_markdown);
  }, [page.id, page.title, page.slug, page.section, page.body_markdown]);

  useEffect(() => {
    const pending = pendingSelectionRef.current;
    if (pending && bodyRef.current) {
      bodyRef.current.focus();
      bodyRef.current.setSelectionRange(pending[0], pending[1]);
      pendingSelectionRef.current = null;
    }
  }, [body]);

  const dirty = title !== page.title || slug !== page.slug || section !== (page.section ?? '') || body !== page.body_markdown;

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const updated = await updateWikiPage(projectId, page.id, {
        title: title.trim() || page.title,
        slug: slug.trim() || undefined,
        body_markdown: body,
        section: section.trim(),
      });
      onSaved(updated);
      setMode((m) => (m === 'history' ? 'edit' : m));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the page.');
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setConfirmingDelete(false);
    setBusy(true);
    setError(null);
    try {
      await deleteWikiPage(projectId, page.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the page.');
      setBusy(false);
    }
  }

  /** Every toolbar action reads/writes through the textarea's own live DOM
   * value rather than the `body` state closure - a controlled input keeps
   * `el.value` and `body` identical at all times, so this is always current
   * even for an action (image upload) that resolves well after the click
   * that started it, without needing a stale-snapshot workaround. */
  function currentSelection() {
    const el = bodyRef.current;
    const value = el ? el.value : body;
    const start = el ? (el.selectionStart ?? value.length) : value.length;
    const end = el ? (el.selectionEnd ?? value.length) : value.length;
    return { el, value, start, end };
  }

  function replaceRange(start: number, end: number, text: string, selectFrom: number, selectTo: number) {
    const { value } = currentSelection();
    pendingSelectionRef.current = [selectFrom, selectTo];
    setBody(value.slice(0, start) + text + value.slice(end));
  }

  function insertAtCursor(snippet: string) {
    const { start, end } = currentSelection();
    replaceRange(start, end, snippet, start + snippet.length, start + snippet.length);
  }

  /** Wraps the current selection in `before`/`after` (e.g. `**`/`**` for
   * bold); with nothing selected, inserts `placeholder` wrapped instead and
   * selects it, so the next keystroke types over it. */
  function wrapSelection(before: string, after: string, placeholder: string) {
    const { start, end, value } = currentSelection();
    const selected = end > start ? value.slice(start, end) : placeholder;
    replaceRange(start, end, before + selected + after, start + before.length, start + before.length + selected.length);
  }

  function insertLink() {
    const { start, end, value } = currentSelection();
    const label = end > start ? value.slice(start, end) : 'link text';
    const url = 'https://';
    const urlStart = start + 1 + label.length + 2; // right after "[label]("
    replaceRange(start, end, `[${label}](${url})`, urlStart, urlStart + url.length);
  }

  /** Adds `prefix` to the start of every line touched by the current
   * selection (or just the current line, with nothing selected) - used for
   * headings, quotes and list items. A line already carrying the prefix is
   * left alone rather than doubled up. */
  function prefixLines(prefix: string) {
    const { start, end, value } = currentSelection();
    const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const nextBreak = value.indexOf('\n', end);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const block = value.slice(lineStart, lineEnd);
    const prefixed = block
      .split('\n')
      .map((l) => (l.startsWith(prefix) ? l : prefix + l))
      .join('\n');
    replaceRange(lineStart, lineEnd, prefixed, lineStart, lineStart + prefixed.length);
  }

  async function onPickImage(file: File) {
    setUploadingImage(true);
    setError(null);
    try {
      const url = await uploadWikiImage(projectId, file);
      insertAtCursor(`![${file.name.replace(/\.[^.]+$/, '')}](${url})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload the image.');
    } finally {
      setUploadingImage(false);
    }
  }

  function onDropImage(e: DragEvent<HTMLTextAreaElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (file) void onPickImage(file);
  }

  /** Ctrl/Cmd+S saves from anywhere in the editor card, not just while the
   * textarea has focus - a title/section/slug edit is just as saveable. */
  function onShortcut(e: KeyboardEvent<HTMLDivElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      if (dirty && !busy) void save();
    }
  }

  const FORMAT_ACTIONS: { label: string; title: string; style?: CSSProperties; run: () => void }[] = [
    { label: 'B', title: 'Bold (wraps selection with **)', style: { fontWeight: 700 }, run: () => wrapSelection('**', '**', 'bold text') },
    { label: 'I', title: 'Italic', style: { fontStyle: 'italic' }, run: () => wrapSelection('*', '*', 'italic text') },
    { label: '</>', title: 'Inline code', run: () => wrapSelection('`', '`', 'code') },
    { label: 'H2', title: 'Heading', run: () => prefixLines('## ') },
    { label: 'Link', title: 'Link', run: () => insertLink() },
    { label: '•', title: 'Bulleted list', run: () => prefixLines('- ') },
    { label: '1.', title: 'Numbered list', run: () => prefixLines('1. ') },
    { label: '❝', title: 'Quote', run: () => prefixLines('> ') },
    { label: 'Table', title: 'Insert a starter table', run: () => insertAtCursor('\n| Column 1 | Column 2 |\n| --- | --- |\n| Cell | Cell |\n') },
    { label: 'TOC', title: 'Insert a table of contents (auto-generated from headings)', run: () => insertAtCursor('[[TOC]]\n\n') },
    { label: '―', title: 'Horizontal rule', run: () => insertAtCursor('\n---\n') },
  ];

  const showTextarea = mode === 'edit' || mode === 'split';
  const showPreview = mode === 'preview' || mode === 'split';

  return (
    // Full-bleed editing surface, not a set-card: no boxed padding around
    // the whole thing, the header/toolbar/footer are thin bordered strips
    // and the middle (textarea/preview/history) takes every remaining pixel
    // of the editor pane - the intent is a real doc/code editor, not a form
    // embedded in a settings page.
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onKeyDown={onShortcut}>
      <div style={{ display: 'flex', gap: 10, padding: '14px 24px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0, flexWrap: 'wrap' }}>
        <input className="ss-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Page title" style={{ flex: 1, minWidth: 160 }} />
        <input
          className="ss-input"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          placeholder="Section (optional)"
          title="Groups this page under a header in the sidebar. Leave blank to keep it ungrouped."
          style={{ width: 160 }}
        />
        <input className="ss-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="page-slug" style={{ width: 180, fontFamily: 'var(--ss-mono)', fontSize: 12.5 }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '8px 24px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {showTextarea &&
            FORMAT_ACTIONS.map((a) => (
              <button
                key={a.label}
                type="button"
                className="ss-btn ss-btn--ghost ss-btn--sm"
                title={a.title}
                onClick={a.run}
                style={{ ...TOOLBAR_BTN_STYLE, ...a.style }}
              >
                {a.label}
              </button>
            ))}
          {showTextarea && (
            <button
              type="button"
              className="ss-btn ss-btn--ghost ss-btn--sm"
              style={TOOLBAR_BTN_STYLE}
              title="Insert an image (or drag one onto the editor)"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploadingImage}
            >
              {uploadingImage ? '…' : 'Image'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <TabStrip
            listLabel="View mode"
            variant="compact"
            tabs={VIEW_MODES}
            activeId={mode}
            onSelect={(id) => setMode(id as EditorMode)}
            controlsPanels
          />
          <button
            type="button"
            className="ss-btn ss-btn--ghost ss-btn--sm"
            onClick={() => setMode(mode === 'history' ? 'edit' : 'history')}
          >
            {mode === 'history' ? 'Close history' : 'History'}
          </button>
        </div>
      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void onPickImage(file);
        }}
      />

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {mode === 'history' ? (
          <div style={{ height: '100%', overflowY: 'auto', padding: '16px 24px' }}>
            <VersionHistoryPanel
              projectId={projectId}
              page={page}
              onRestored={(restored) => {
                onSaved(restored);
                setMode('edit');
              }}
            />
          </div>
        ) : (
          <div id={panelId(mode)} style={{ height: '100%', display: mode === 'split' ? 'grid' : 'flex', gridTemplateColumns: mode === 'split' ? '1fr 1fr' : undefined }}>
            {showTextarea && (
              <textarea
                ref={bodyRef}
                className="ss-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDropImage}
                placeholder="Write in markdown… or drag an image in."
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  background: 'transparent',
                  border: 'none',
                  borderRight: mode === 'split' ? '1px solid var(--ss-border-soft)' : undefined,
                  borderRadius: 0,
                  padding: '20px 24px',
                  fontFamily: 'var(--ss-mono)',
                  fontSize: 13,
                  lineHeight: 1.6,
                  resize: 'none',
                  outline: dragOver ? '2px dashed var(--ss-accent)' : undefined,
                  outlineOffset: dragOver ? -2 : undefined,
                }}
              />
            )}
            {showPreview && (
              <div style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', padding: '20px 28px' }}>
                <MarkdownView source={body} />
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: '10px 24px', borderTop: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
        <button
          className="ss-btn ss-btn--danger ss-btn--sm"
          onClick={() => setConfirmingDelete(true)}
          disabled={busy}
        >
          Delete page
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span className="hint" style={{ color: body.length > BODY_CHAR_SOFT_CAP ? 'var(--ss-danger)' : undefined }}>
            {body.length.toLocaleString()} / {BODY_CHAR_SOFT_CAP.toLocaleString()} characters
          </span>
          {/* Separate live region from the character count above, which
              changes on every keystroke - announcing that on every character
              would be exactly the kind of aria-live spam screen reader users
              turn assistive tech off for. This only mutates (so only
              announces) on a real dirty/clean transition. */}
          <span aria-live="polite" role="status">
            {dirty && (
              <span className="hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ss-accent)', flexShrink: 0 }} />
                Unsaved changes
              </span>
            )}
          </span>
          <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={() => void save()} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save page'}
          </button>
        </div>
      </div>
      {error && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 13, padding: '0 24px 12px' }} role="alert">
          {error}
        </p>
      )}
      {confirmingDelete && (
        <EdConfirmDialog
          title={`Delete "${page.title}"?`}
          body="This removes the page permanently."
          confirmLabel="Delete page"
          onConfirm={() => void doDelete()}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </div>
  );
}

function PageList({
  pages,
  selectedId,
  onSelect,
  onCreate,
  onReorder,
  error,
}: {
  pages: WikiPageSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onReorder: (ids: string[]) => void;
  error: string | null;
}) {
  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= pages.length) return;
    const ids = pages.map((p) => p.id);
    const [item] = ids.splice(index, 1);
    ids.splice(target, 0, item!);
    onReorder(ids);
  }

  return (
    <aside className="wiki-sidebar">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 10px' }}>
        <h3 style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.03em', color: 'var(--ss-text-2)' }}>
          Pages{pages.length > 0 ? ` (${pages.length})` : ''}
        </h3>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ padding: '4px 10px', fontSize: 11.5 }} onClick={onCreate}>
          + New
        </button>
      </div>
      {pages.length === 0 && (
        <span className="hint" style={{ padding: '0 4px' }}>
          No pages yet. The first page becomes the wiki's home.
        </span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {pages.map((p, i) => {
          // Pages keep one flat, position-ordered list (reordering and "which
          // page is home" both operate on that single order); section is
          // purely a rendering label, shown as a header whenever it changes
          // from the page before it in that same order - not a resort, and
          // not a tree. Ungrouped pages (section null) get no header.
          const previousSection = i > 0 ? pages[i - 1]!.section : undefined;
          const showHeader = p.section && p.section !== previousSection;
          return (
            <div key={p.id}>
              {showHeader && (
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--ss-text-3)',
                    padding: '10px 10px 4px',
                  }}
                >
                  {p.section}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  background: p.id === selectedId ? 'var(--ss-accent-soft)' : 'transparent',
                }}
              >
                <button
                  onClick={() => onSelect(p.id)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    padding: '8px 10px',
                    fontSize: 13,
                    fontWeight: p.id === selectedId ? 600 : 500,
                    color: p.id === selectedId ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={i === 0 ? `${p.title} (home page)` : p.title}
                >
                  {i === 0 ? `${p.title} · Home` : p.title}
                </button>
                <button
                  aria-label={`Move ${p.title} up`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.35 : 1, padding: '2px 4px' }}
                >
                  ↑
                </button>
                <button
                  aria-label={`Move ${p.title} down`}
                  disabled={i === pages.length - 1}
                  onClick={() => move(i, 1)}
                  style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', cursor: i === pages.length - 1 ? 'default' : 'pointer', opacity: i === pages.length - 1 ? 0.35 : 1, padding: '2px 4px' }}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {error && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 12, padding: '8px 4px 0' }} role="alert">
          {error}
        </p>
      )}
    </aside>
  );
}

export function ScreenProjectWiki() {
  const { projectId } = useWorkspace();
  const [state, setState] = useState<WikiLoadState>({ status: 'loading' });
  const [pages, setPages] = useState<WikiPageSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<WikiPage | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [pageDirty, setPageDirty] = useState(false);

  useUnsavedChangesGuard(pageDirty);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    setPages([]);
    setSelectedId(null);
    setSelectedPage(null);
    getWiki(projectId)
      .then(async (wiki) => {
        if (cancelled) return;
        if (!wiki) {
          setState({ status: 'none' });
          return;
        }
        setState({ status: 'ready', wiki });
        const list = await listWikiPages(projectId).catch(() => []);
        if (cancelled) return;
        setPages(list);
        if (list.length > 0) setSelectedId(list[0]!.id);
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'none' });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedId) {
      setSelectedPage(null);
      return;
    }
    getWikiPage(projectId, selectedId)
      .then((page) => {
        if (!cancelled) setSelectedPage(page);
      })
      .catch(() => {
        if (!cancelled) setSelectedPage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, selectedId]);

  const wiki = state.status === 'ready' ? state.wiki : null;

  async function createPage() {
    setListError(null);
    try {
      const page = await createWikiPage(projectId, { title: `New page ${pages.length + 1}` });
      setPages((prev) => [...prev, { id: page.id, slug: page.slug, title: page.title, position: page.position, section: page.section }]);
      setSelectedId(page.id);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Could not create the page.');
    }
  }

  async function reorder(ids: string[]) {
    const previous = pages;
    // Optimistic reorder so the arrows feel immediate; reconciled with the
    // server's own ordering once the request resolves, or reverted on failure.
    setPages((prev) => ids.map((id) => prev.find((p) => p.id === id)!).filter(Boolean));
    try {
      setPages(await reorderWikiPages(projectId, ids));
    } catch (err) {
      setPages(previous);
      setListError(err instanceof Error ? err.message : 'Could not reorder pages.');
    }
  }

  const selectedIndex = useMemo(() => pages.findIndex((p) => p.id === selectedId), [pages, selectedId]);

  return (
    <PjShell tab="wiki">
      {wiki ? (
        // Full-bleed within pj-main, deliberately not wrapped in pj-page's
        // padded/max-width box - this is a shell specific to the Wiki tab's
        // own content area (the outer pj-rail from PjShell is untouched),
        // giving the page-list sidebar and the editor room to reach edge to
        // edge instead of sharing a centred content column.
        <div className="wiki-shell">
          <WikiSettingsBar
            wiki={wiki}
            onChanged={(w) => setState({ status: 'ready', wiki: w })}
            onDeleted={() => {
              setState({ status: 'none' });
              setPages([]);
              setSelectedId(null);
            }}
          />
          <div className="wiki-shell-body">
            <PageList
              pages={pages}
              selectedId={selectedId}
              onSelect={(id) => {
                if (id === selectedId) return;
                if (!confirmNavigation()) return;
                setSelectedId(id);
              }}
              onCreate={() => {
                if (!confirmNavigation()) return;
                void createPage();
              }}
              onReorder={(ids) => void reorder(ids)}
              error={listError}
            />
            {selectedPage ? (
              <PageEditor
                key={selectedPage.id}
                page={selectedPage}
                onDirtyChange={setPageDirty}
                onSaved={(updated) => {
                  setSelectedPage(updated);
                  setPages((prev) =>
                    prev.map((p) => (p.id === updated.id ? { id: updated.id, slug: updated.slug, title: updated.title, position: updated.position, section: updated.section } : p)),
                  );
                }}
                onDeleted={() => {
                  setPages((prev) => prev.filter((p) => p.id !== selectedPage.id));
                  setSelectedId(pages[selectedIndex - 1]?.id ?? pages[selectedIndex + 1]?.id ?? null);
                }}
              />
            ) : (
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="hint">Select a page to edit it, or create one to get started.</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="pj-page">
          <div className="pj-page-head">
            <div>
              <h1>Wiki</h1>
              <p>Lore, guides and reference pages, hosted at their own URL.</p>
            </div>
          </div>

          {state.status === 'loading' && (
            // Not .studio-loading here - that class is 100vh tall, sized for
            // its other two call sites (RequireAuth's full-screen auth gate,
            // ProjectProvider's full-app boot), both of which own the whole
            // viewport. Nested under pj-page-head inside the scrollable
            // pj-main, a 100vh block pushed this text off-screen and made an
            // already-loading tab look like it had an extra scrollbar.
            <CenteredLoading minHeight="50vh" />
          )}

          {state.status === 'none' && (
            <CreateWikiPanel onCreated={(w) => setState({ status: 'ready', wiki: w })} />
          )}
        </div>
      )}
    </PjShell>
  );
}
